<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'attachments',
    path: '/attachments',
    component: 'AttachmentBrowser',
    title: () => pgettext('AttachmentsView', 'Attachments'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'AttachmentBrowser',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const selectedItems = ref([]);
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
    resizable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'key',
      name: $fn.value.pgettext('AttachmentsView', 'Key'),
    },
    {
      field: 'object_type',
      name: $fn.value.pgettext('AttachmentsView', 'Object type'),
    },
    {
      field: 'file_name',
      name: $fn.value.pgettext('AttachmentsView', 'File name'),
    },
    {
      field: 'file_type',
      name: $fn.value.pgettext('AttachmentsView', 'File type'),
    },
    {
      field: 'file_size',
      name: $fn.value.pgettext('AttachmentsView', 'File size'),
    },
    {
      field: 'description',
      name: $fn.value.pgettext('AttachmentsView', 'Description'),
    },
    {
      field: 'created_at',
      name: $fn.value.pgettext('AttachmentsView', 'Create time'),
      formatter: {
        name: 'datetime',
      },
    },
    {
      field: 'created_by',
      name: $fn.value.pgettext('AttachmentsView', 'Created by'),
      formatter: {
        name: 'user',
      },
    },
  ]);

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = async () => {
    return $fn.value.slickDataSource('list_attachment');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      selectedItems,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('AttachmentsView', 'Attachments'),
      icon: $config.value.attachment.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_ikeuto72h_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_6ly4rclr4_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };

  const act_7315092382398562_closeModal_ikeuto72h =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_ikeuto72h_getActionArgs,
    });
  const inst_evh_7315092382398562_close_dh8fvirh5 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_ikeuto72h,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_dh8fvirh5],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_tg90qwhzw =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_x6m9n8jp9 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_tg90qwhzw,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_x6m9n8jp9],
    event: 'reload',
  });
  const act_2248226175642056_setData_6ly4rclr4 = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_6ly4rclr4_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_9zc9csq0u =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_6ly4rclr4,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_9zc9csq0u],
      event: 'selectedRowsChanged',
    });

  const actions = {
    inst_evh_7315092382398562_close_dh8fvirh5,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_x6m9n8jp9,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_9zc9csq0u,
    inst_evh_2248226175642056_selectedRowsChanged,
  };

  const evh_7315092382398562_close_dh8fvirh5 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_dh8fvirh5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_x6m9n8jp9 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_x6m9n8jp9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_9zc9csq0u = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_9zc9csq0u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="AttachmentBrowser"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <VueSlickgrid
      name="attachments"
      :headerTitle="pgettext('AttachmentsView', 'Attachments')"
      :items="ds"
      checkbox-row-selection
      export-to-csv
      :exportToCsvFilename="pgettext('ExportFilename', 'attachments')"
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2">
          <PageHelp path="/products/products.html"> </PageHelp>
        </div>
      </template>
    </VueSlickgrid>
  </ItpPage>
</template>
